import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { deleteGame, type GameItem } from '../../api/games';

interface Props {
  game: GameItem;
  onClose: () => void;
  onDeleted: () => void;
}

export function DeleteGameDialog({ game, onClose, onDeleted }: Props) {
  const qc = useQueryClient();
  const [purgeBackups, setPurgeBackups] = useState(false);
  const [purgeInstall, setPurgeInstall] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiresTypedConfirm = purgeBackups || purgeInstall;
  const typedOk = !requiresTypedConfirm || confirmText === game.id;

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteGame(game.id, {
        purge_firewall: true,
        purge_backups: purgeBackups,
        purge_install: purgeInstall,
      });
      await qc.invalidateQueries({ queryKey: ['games'] });
      onDeleted();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Game — {game.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {game.installing && (
            <Alert severity="warning">
              This game is currently installing. Deleting it will abort the installation.
            </Alert>
          )}
          <Alert severity="error">
            This will permanently remove the game configuration and stop the server.
            UFW firewall rules will be removed.
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={purgeBackups}
                onChange={(e) => setPurgeBackups(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                <strong>Delete all backups</strong> — permanently removes backup archives
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={purgeInstall}
                onChange={(e) => setPurgeInstall(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                <strong>Delete install directory</strong> — permanently removes{' '}
                <code>/opt/gameservers/{game.id}</code> (all game files, saves)
              </Typography>
            }
          />

          {requiresTypedConfirm && (
            <TextField
              label={`Type "${game.id}" to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              size="small"
              fullWidth
              error={confirmText.length > 0 && confirmText !== game.id}
              helperText={confirmText.length > 0 && confirmText !== game.id ? 'Game ID does not match' : ''}
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={busy || !typedOk}
          onClick={() => void handleDelete()}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Delete Game
        </Button>
      </DialogActions>
    </Dialog>
  );
}
